import { createStytchB2BHeadlessClient } from "@stytch/vanilla-js/b2b/headless";

export const stytchClient = createStytchB2BHeadlessClient(
  import.meta.env.VITE_STYTCH_PUBLIC_TOKEN ?? "",
);

export const STYTCH_ORG_ID: string = import.meta.env.VITE_STYTCH_ORGANIZATION_ID ?? "";

export const STYTCH_SESSION_DURATION_MINUTES = 5;

// Deferred to call time to avoid accessing `window` at module parse (e.g. in tests).
export function getAppOrigin(): string {
  return import.meta.env.VITE_APP_URL?.replace(/\/$/, "") ?? window.location.origin;
}

const EXPIRED_LINK_MSG = "This link has expired or already been used. Please request a new one.";
export const INVALID_CREDENTIALS_MSG = "Invalid email or password.";

const ACCOUNT_LOOKUP_ERROR_TYPES = new Set([
  "member_not_found",
  "organization_user_not_found",
  "user_not_found",
]);

const STYTCH_ERROR_MESSAGES: Record<string, string> = {
  magic_link_not_found: EXPIRED_LINK_MSG,
  token_not_found: EXPIRED_LINK_MSG,
  magic_link_already_used: EXPIRED_LINK_MSG,
  reset_password: "Your password needs to be reset. Check your email for a reset link.",
  organization_not_found: "Organization not found. Contact your administrator.",
  member_not_found: INVALID_CREDENTIALS_MSG,
  organization_user_not_found: INVALID_CREDENTIALS_MSG,
  unauthorized_credentials: INVALID_CREDENTIALS_MSG,
  user_not_found: INVALID_CREDENTIALS_MSG,
  reset_password_expired: "This reset link has expired. Request a new one.",
  breached_password: "This password has appeared in a data breach. Please choose a different one.",
  weak_password: "This password isn't strong enough. Follow the suggestions below and try again.",
};

function getStytchErrorType(err: Record<string, unknown>): string | null {
  return typeof err.error_type === "string" ? err.error_type : null;
}

function getStytchRawMessage(err: Record<string, unknown>): string {
  if (typeof err.error_message === "string") return err.error_message;
  if (typeof err.message === "string") return err.message;
  return "";
}

function isAccountLookupRawMessage(rawMessage: string): boolean {
  const lower = rawMessage.toLowerCase();
  return (
    lower.includes("email could not be found") ||
    lower.includes("email address not found") ||
    lower.includes("member not found") ||
    lower.includes("user not found")
  );
}

export function isStytchAccountLookupError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as Record<string, unknown>;
  const errorType = getStytchErrorType(e);
  if (errorType && ACCOUNT_LOOKUP_ERROR_TYPES.has(errorType)) return true;
  return isAccountLookupRawMessage(getStytchRawMessage(e));
}

export function getStytchErrorMessage(err: unknown, fallback: string): string {
  if (!err || typeof err !== "object") return fallback;
  const e = err as Record<string, unknown>;

  const errorType = getStytchErrorType(e);
  if (errorType && errorType in STYTCH_ERROR_MESSAGES) {
    return STYTCH_ERROR_MESSAGES[errorType];
  }

  const rawMessage = getStytchRawMessage(e);
  if (isAccountLookupRawMessage(rawMessage)) return INVALID_CREDENTIALS_MSG;
  if (rawMessage.includes("already used")) return EXPIRED_LINK_MSG;

  return fallback;
}
